import { createContext, useContext } from "react";

import type { ProductItem } from "~/lib/app.types";

import type { SnapEntry } from "./basket-rows-derive";

export type UnifiedRow =
  | { kind: "normal"; lineItem: ProductItem }
  | { kind: "removed"; itemId: string; snap: SnapEntry };

export type BasketRowsApi = {
  rows: UnifiedRow[];
  removeLineItem: (lineItem: ProductItem, originalIndex?: number) => void;
  // Save-for-Later move: captures a snap with source:"savedForLater" and
  // submits the appropriate path (auth saveLineItem / guest guestAdd + remove).
  // The snap card's "Add Item Back" reverses the move.
  saveForLater: (lineItem: ProductItem, originalIndex?: number) => void;
  // Immediate snap removal (no animation). Card calls this after a successful
  // re-add (the item returns as a normal row, which is the visual feedback);
  // also used internally for settle-observer rollbacks + reconcile.
  dropSnap: (itemId: string) => void;
  // Successful "Add Item Back" removal plus a row-order hint for the replacement
  // basket line. SCAPI appends newly-added lines; this keeps the visual cart
  // order at the snap's captured original slot.
  restoreSnap: (itemId: string, replacementItemId?: string) => void;
  // Animated dismissal (XD-189): marks the snap "leaving" so the card collapses,
  // then removes it. Used by the card's ✕ button and the auto-dismiss timer /
  // navigation handler.
  dismissSnap: (itemId: string) => void;
  // Card writes its own re-add error here (validation error on the `add`
  // submission).
  setSnapError: (itemId: string, message: string) => void;
  snapErrors: ReadonlyMap<string, string>;
  // itemIds whose snap card is mid collapse-animation (XD-189). The card reads
  // this to apply the collapse styles.
  leaving: ReadonlySet<string>;
  // itemIds whose line-item tile is playing the Save-for-Later lift/slide/fade
  // exit animation (XD-189). The row layout reads this to animate the tile out.
  savingForLater: ReadonlySet<string>;
};

export const BasketRowsContext = createContext<BasketRowsApi | null>(null);
export const useBasketRows = () => useContext(BasketRowsContext);
